/**
 * Utility functions for tracking A/B test events and conversions
 */

/**
 * Track a conversion event for the current A/B test variant
 * @param eventName The name of the conversion event (e.g., 'signup', 'demo_booked')
 * @param additionalData Optional additional data to track with the event
 */
export const trackConversion = (eventName: string, additionalData: Record<string, any> = {}) => {
  try {
    // Get the current variant from localStorage
    const variant = localStorage.getItem('abtest_landing_page_variations');
    
    if (!variant) {
      console.warn('No A/B test variant found in localStorage');
      return;
    }
    
    // Track the conversion in analytics
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', eventName, {
        ...additionalData,
        ab_test_variant: variant,
        event_category: 'conversion',
      });
    }
    
    console.log(`Tracked conversion event "${eventName}" for variant "${variant}"`, additionalData);
  } catch (e) {
    console.error('Failed to track conversion:', e);
  }
};

/**
 * Track an engagement event for the current A/B test variant
 * @param eventName The name of the engagement event (e.g., 'cta_click', 'scroll_depth')
 * @param additionalData Optional additional data to track with the event
 */
export const trackEngagement = (eventName: string, additionalData: Record<string, any> = {}) => {
  try {
    // Get the current variant from localStorage
    const variant = localStorage.getItem('abtest_landing_page_variations');
    
    if (!variant) {
      console.warn('No A/B test variant found in localStorage');
      return;
    }
    
    // Track the engagement in analytics
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', eventName, {
        ...additionalData,
        ab_test_variant: variant,
        event_category: 'engagement',
      });
    }
    
    console.log(`Tracked engagement event "${eventName}" for variant "${variant}"`, additionalData);
  } catch (e) {
    console.error('Failed to track engagement:', e);
  }
};

/**
 * Get the current A/B test variant
 * @returns The current variant or null if not set
 */
export const getCurrentVariant = (): string | null => {
  return localStorage.getItem('abtest_landing_page_variations');
};

// Define types for window.gtag
declare global {
  interface Window {
    gtag: (command: string, action: string, params?: any) => void;
  }
} 